import { Navigate, Outlet, createFileRoute, useMatch } from '@tanstack/react-router'

import { useLocalStore } from '~/stores'
import { userPreferencesQueryOptions } from '~/queries/user'
import { setAccountPreference } from '~/services/preferences/userPreferences'
import { queryClient } from '~/providers/ReactQuery'
import { setTag } from '@sentry/react'
import { getQueryKey, useSuspenseQuery } from '~/services'
import { resolveKey } from '~/services/preferences/preferences'
import { getUseBotOnboardingStore } from '~/features/bots/stores/useBotOnboardingStore'
import { useGetWorkspacePreferences } from '~/hooks/users/useGetWorkspacePreferences'
import { useIsAdkBot } from '~/features/bots/hooks/useIsAdkBot'
import { useFeature } from '~/hooks/useFeature'
import { useEffect } from 'react'

export const Route = createFileRoute('/workspaces/$workspaceId/bots/$botId')({
  loader: ({ params }) => {
    setTag('botId', params.botId)
    const useBotOnboardingStore = getUseBotOnboardingStore({ botId: params.botId, workspaceId: params.workspaceId })
    const onboardingStep = useBotOnboardingStore.getState().deploymentProgress.currentStep
    return { onboardingStep }
  },
  component: BotRouteComponent,
})

function useTrackBotVisit(workspaceId: string, botId: string) {
  useEffect(() => {
    let cancelled = false
    useLocalStore.getState().setLastActiveWorkspaceAndBotIds(workspaceId, botId)

    const preferenceProps = { path: '$workspaceId/botHistory', params: { workspaceId } } as const
    void (async () => {
      const botHistory = await queryClient.ensureQueryData(userPreferencesQueryOptions(preferenceProps))
      if (cancelled) return
      const newHistory = [botId, ...botHistory.filter((id) => id !== botId)].slice(0, 50)
      const queryKey = getQueryKey('preferences/$path', { path: resolveKey(preferenceProps) })
      queryClient.setQueryData(queryKey, () => newHistory)
      await setAccountPreference({ ...preferenceProps, value: newHistory }).catch(() => {})
    })()
    return () => {
      cancelled = true
    }
  }, [workspaceId, botId])
}

function BotRouteComponent() {
  const { botId, workspaceId } = Route.useParams()
  useTrackBotVisit(workspaceId, botId)
  const { data: bot } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_', { botId, workspaceId })
  const { data: isBotOnboarding } = useGetWorkspacePreferences(
    { path: 'isOnboarding/$botId', params: { botId } },
    workspaceId
  )
  const isStudioOnboardingEnabled = useFeature('studio-onboarding')

  const { onboardingStep } = Route.useLoaderData()
  const newBotDeployComplete = onboardingStep === 'completed'
  const newBotDeployAck = onboardingStep === 'acknowledged'

  const onNewBot = !!useMatch({
    from: '/workspaces/$workspaceId/bots/$botId/new-bot',
    shouldThrow: false,
  })

  const onNewChat = !!useMatch({
    from: '/workspaces/$workspaceId/bots/$botId/new-chat',
    shouldThrow: false,
  })

  const onAdkSetup = !!useMatch({
    from: '/workspaces/$workspaceId/bots/$botId/new-adk-bot-setup',
    shouldThrow: false,
  })

  // ADK bot redirect: if ADK bot and still in onboarding, redirect to setup
  const isAdkBot = useIsAdkBot({ workspaceId, botId })
  const { data: isAdkOnboarding } = useGetWorkspacePreferences(
    { path: 'isAdkOnboarding/$botId', params: { botId } },
    workspaceId
  )

  if (isAdkBot && isAdkOnboarding && !onAdkSetup) {
    return (
      <Navigate params={{ workspaceId, botId }} to="/workspaces/$workspaceId/bots/$botId/new-adk-bot-setup" replace />
    )
  }

  if (!isBotOnboarding || bot.dev || (isBotOnboarding && onNewBot) || isStudioOnboardingEnabled) {
    return <Outlet />
  }

  if ((newBotDeployComplete || newBotDeployAck) && onNewChat) {
    return <Outlet />
  }

  return <Navigate params={{ workspaceId, botId }} to="/workspaces/$workspaceId/bots/$botId/new-bot" replace />
}
